import type { ReactNode } from 'react'
import { Line } from '@react-three/drei'
import type { PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { ESPESSURA_PAINEL_TETO } from '../../dominio/didatico/perfilVeiculo'
import { COLUNAS_TETO_LATERAL, type EstadoTetoLateral } from '../../dominio/didatico/rebatimentoLateralTeto'
import { deArray } from '../../dominio/geometria/vetor3'
import { Barra } from '../componentes/Barra'
import { MaterialPeca } from '../componentes/Peca'
import { Rotulo3D } from '../componentes/Rotulo3D'
import { FaceDeCorte } from '../veiculo/Seccionado'
import { cortesAlivioTeto, pontosTetoLateral, trechosLongarinaTetoInferior } from './zonasTetoLateral'

function GrupoRebatido({ perfil, progresso, children }: { perfil: PerfilVeiculo; progresso: number; children: ReactNode }) {
  const { pivo } = pontosTetoLateral(perfil)
  return <group position={pivo} rotation={[0, 0, -Math.PI / 2 * progresso]}>
    <group position={[-pivo[0], -pivo[1], -pivo[2]]}>{children}</group>
  </group>
}

/** Montantes superiores separados fisicamente; os montantes inferiores permanecem no veículo. */
export function EstruturaTetoLateral3D({ perfil, estado }: { perfil: PerfilVeiculo; estado: EstadoTetoLateral }) {
  const p = pontosTetoLateral(perfil)
  const folga = 0.014
  return <group name="estrutura-teto-lateral">
    {COLUNAS_TETO_LATERAL.map((c) => <group key={c} name={`coluna-${c}-teto-lateral`}>
      {([-1, 1] as const).map((sinal) => {
        const cortada = sinal === -1 && estado.cortes[c]
        const id = `coluna${c === 'D' ? 'C' : c}${sinal > 0 ? 'Esquerda' : 'Direita'}`
        return <group key={sinal}>
          <Barra id={id} de={deArray(p.pontoColuna(c, sinal, perfil.topoSoleiraY))}
            para={deArray(p.pontoColuna(c, sinal, cortada ? p.yCorte - folga : perfil.tetoY))} largura={0.10} espessura={0.11} />
          {cortada && <>
            <FaceDeCorte posicao={p.pontoColuna(c, sinal, p.yCorte - folga)} eixo="y" raio={0.052} />
            <GrupoRebatido perfil={perfil} progresso={estado.progressoRebatimento}>
              <Barra id={id} de={deArray(p.pontoColuna(c, sinal, p.yCorte + folga))} para={deArray(p.pontoColuna(c, sinal, perfil.tetoY))} largura={0.10} espessura={0.11} />
              <FaceDeCorte posicao={p.pontoColuna(c, sinal, p.yCorte + folga)} eixo="y" raio={0.052} />
            </GrupoRebatido>
          </>}
        </group>
      })}
      <Rotulo3D posicao={p.pontoColuna(c, -1, p.yCorte - 0.07)} texto={c} subtexto={estado.cortes[c] ? 'Cortada' : undefined} variante="estrutura" sempreVisivel fator={2.2} />
    </group>)}
    <GrupoRebatido perfil={perfil} progresso={estado.progressoRebatimento}>
      {[perfil.teto.zDianteiro, perfil.colunaB.z, perfil.teto.zTraseiro].map((z) => <Barra key={z} id="reforcosTeto" de={deArray([-p.xInferior, perfil.tetoY - 0.03, z])} para={deArray([p.xInferior, perfil.tetoY - 0.03, z])} largura={0.08} espessura={0.06} />)}
      {[-1, 1].flatMap((sinal) => (sinal > 0 ? trechosLongarinaTetoInferior(perfil, estado.alivioA, estado.alivioD) : [[perfil.teto.zTraseiro, perfil.teto.zDianteiro]])
        .map(([de, ate], i) => <Barra key={`${sinal}:${i}`} id="reforcosTeto" de={deArray([sinal * p.xInferior, perfil.tetoY - 0.03, de])} para={deArray([sinal * p.xInferior, perfil.tetoY - 0.03, ate])} largura={0.07} espessura={0.07} />))}
    </GrupoRebatido>
  </group>
}

/** Chapas com duas fendas de alívio junto à borda inferior; sem CSG ou física adicionais. */
export function ChapaTetoLateral3D({ perfil, estado }: { perfil: PerfilVeiculo; estado: EstadoTetoLateral }) {
  const p = pontosTetoLateral(perfil)
  const meia = p.xInferior + 0.04
  const cortes = cortesAlivioTeto(perfil, estado.alivioA, estado.alivioD)
  const faixas: Array<{ de: number; ate: number; x: number; largura: number }> = []
  let inicio = perfil.teto.zTraseiro
  for (const corte of cortes) {
    if (corte.de > inicio) faixas.push({ de: inicio, ate: corte.de, x: 0, largura: 2 * meia })
    const esquerda = corte.x - corte.largura / 2
    const direita = corte.x + corte.largura / 2
    faixas.push({ de: corte.de, ate: corte.ate, x: (esquerda - meia) / 2, largura: esquerda + meia })
    faixas.push({ de: corte.de, ate: corte.ate, x: (direita + meia) / 2, largura: meia - direita })
    inicio = corte.ate
  }
  if (inicio < perfil.teto.zDianteiro) faixas.push({ de: inicio, ate: perfil.teto.zDianteiro, x: 0, largura: 2 * meia })
  return <GrupoRebatido perfil={perfil} progresso={estado.progressoRebatimento}>
    <group name="chapa-teto-rebatida">
      {faixas.map((f, i) => <mesh key={i} position={[f.x, perfil.tetoY, (f.de + f.ate) / 2]}>
        <boxGeometry args={[f.largura, ESPESSURA_PAINEL_TETO, f.ate - f.de]} />
        <MaterialPeca id="teto" corBase="#94a3b8" acabamento="pintura" />
      </mesh>)}
      {cortes.map((corte) => <group key={corte.de}>
        <Line points={[[corte.x, perfil.tetoY + 0.016, corte.de], [corte.x, perfil.tetoY + 0.016, corte.ate]]} color="#f4ca65" lineWidth={5} />
        <Rotulo3D posicao={[corte.x - 0.08, perfil.tetoY + 0.13, (corte.de + corte.ate) / 2]}
          texto="Corte horizontal" subtexto={corte.de < p.centroZ ? 'Alívio D' : 'Alívio A'} variante="achado" sempreVisivel fator={1.7} />
      </group>)}
    </group>
    {estado.rebatido && <Rotulo3D posicao={[0, perfil.tetoY + 0.16, p.centroZ]} texto="Teto rebatido" subtexto="Vão lateral ampliado" variante="achado" sempreVisivel fator={3} />}
  </GrupoRebatido>
}
